import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, getOrganizationSchema, getServiceSchema } from '../../utils/seo';

const HomePage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeService, setActiveService] = useState(0);

  useSEO({
    title: 'Limpieza Post Mortem Barcelona | BIOTRAUMA POST MORTEM',
    description: 'Servicio profesional de limpieza post mortem en Barcelona y provincia. Desinfección, saneamiento y neutralización de olores con máxima discreción. Respuesta 24/7.',
    keywords: 'limpieza post mortem, limpieza por fallecimiento, desinfección profesional, Barcelona, saneamiento, neutralización olores',
    canonical: '/',
    ogImage: 'https://public.readdy.ai/ai/img_res/40bbcb3f4cca389b3686ba07e882ce27.jpg',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        getOrganizationSchema(),
        getServiceSchema()
      ]
    }
  });

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Limpieza Post Mortem',
      description: 'Intervención especializada tras fallecimiento con protocolos profesionales y equipos de protección individual certificados.',
      features: ['Evaluación inicial', 'Limpieza profunda', 'Desinfección completa', 'Documentación del proceso']
    },
    {
      icon: 'ri-virus-line',
      title: 'Desinfección y Saneamiento',
      description: 'Desinfección profesional del inmueble según normativa sanitaria vigente, eliminando cualquier riesgo biológico.',
      features: ['Productos homologados', 'Técnicas certificadas', 'Cumplimiento normativo', 'Certificado de desinfección']
    },
    {
      icon: 'ri-contrast-drop-line',
      title: 'Neutralización de Olores',
      description: 'Eliminación completa de olores mediante técnicas especializadas de ozonización y neutralización molecular.',
      features: ['Ozonización profesional', 'Neutralizadores enzimáticos', 'Ventilación controlada', 'Verificación final']
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Retirada de Enseres',
      description: 'Vaciado completo del inmueble y retirada de enseres cuando la situación lo requiere, con gestión de residuos.',
      features: ['Clasificación de objetos', 'Embalaje seguro', 'Transporte autorizado', 'Gestión de residuos']
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Limpieza de Choque',
      description: 'Intervención intensiva cuando la situación requiere una actuación inmediata y exhaustiva del espacio.',
      features: ['Respuesta inmediata', 'Equipo especializado', 'Trabajo intensivo', 'Resultados garantizados']
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Preparación para Entrega',
      description: 'Acondicionamiento final del inmueble para su entrega, venta o alquiler, coordinando con otros profesionales.',
      features: ['Limpieza final', 'Coordinación con pintura', 'Pequeñas reparaciones', 'Entrega lista para uso']
    }
  ];

  const steps = [
    {
      number: 1,
      title: 'Evaluación',
      description: 'Valoración inicial del inmueble y determinación del alcance de la intervención necesaria.',
      icon: 'ri-search-eye-line'
    },
    {
      number: 2,
      title: 'Planificación',
      description: 'Elaboración del plan de actuación, presupuesto detallado y coordinación con familia o administración.',
      icon: 'ri-file-list-3-line'
    },
    {
      number: 3,
      title: 'Intervención',
      description: 'Ejecución del servicio con protocolos profesionales, EPIs certificados y máxima discreción.',
      icon: 'ri-tools-line'
    },
    {
      number: 4,
      title: 'Revisión',
      description: 'Verificación final del trabajo realizado, entrega de documentación y certificados si procede.',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  const zones = [
    'Sant Martí', 'Eixample', 'Gràcia', 'Sants-Montjuïc', 'Sarrià-Sant Gervasi',
    'Les Corts', 'Horta-Guinardó', 'Nou Barris', 'Sant Andreu', 'Ciutat Vella',
    'L\'Hospitalet', 'Badalona', 'Santa Coloma', 'Sant Adrià', 'Cornellà',
    'El Prat', 'Sant Boi', 'Castelldefels', 'Gavà', 'Viladecans',
    'Sant Cugat', 'Cerdanyola', 'Rubí', 'Terrassa', 'Sabadell',
    'Mataró', 'Granollers', 'Mollet', 'Martorell'
  ];

  const testimonials = [
    {
      initial: 'M.R.',
      zone: 'Eixample',
      text: 'Profesionalidad absoluta en un momento muy difícil. Realizaron todo el trabajo con una discreción impecable y nos ayudaron con todos los trámites. Muy agradecidos.'
    },
    {
      initial: 'J.L.',
      zone: 'Sant Martí',
      text: 'El servicio fue rápido y eficiente. Nos mantuvieron informados en todo momento y el resultado final fue excelente. Totalmente recomendable.'
    },
    {
      initial: 'A.S.',
      zone: 'L\'Hospitalet',
      text: 'Trato humano y respetuoso. Se encargaron de todo y nos quitaron una gran carga en un momento complicado. Gracias por vuestra profesionalidad.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20cleaning%20team%20protective%20equipment&width=1920&height=1080&seq=hero-biotrauma-v2&orientation=landscape"
            alt="Equipo profesional BIOTRAUMA"
            className="w-full h-full object-cover object-top"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark/80 via-dark/70 to-dark/80"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-4xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8"
            >
              <i className="ri-time-line text-primary"></i>
              <span className="text-white text-sm font-medium">Respuesta 24/7 · Barcelona y Provincia</span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display font-bold text-5xl lg:text-7xl text-white mb-6 leading-tight"
            >
              Servicio profesional de{' '}
              <span className="text-primary">limpieza post mortem</span>{' '}
              en Barcelona
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-300 mb-10 leading-relaxed font-light max-w-3xl"
            >
              Intervención especializada, <strong className="font-semibold text-white">desinfección profesional</strong> y <strong className="font-semibold text-white">saneamiento del inmueble</strong> tras <a href="https://ajuntament.barcelona.cat/omic/es/servicios-funerarios" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline cursor-pointer">fallecimiento</a>. Máxima discreción, protocolos certificados y coordinación total con familia y administración. <Link to="/empresa" className="text-primary hover:underline cursor-pointer">Conozca más sobre nuestra empresa</Link>.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="tel:+34629501941"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-primary/50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl mr-3"></i>
                Llamar Ahora
              </a>
              <a
                href="https://wa.me/34629501941?text=Hola,%20necesito%20información%20sobre%20limpieza%20post%20mortem"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white hover:bg-white hover:text-dark transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl mr-3"></i>
                WhatsApp
              </a>
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/50 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Pedir Valoración
                <i className="ri-arrow-right-line text-xl ml-3"></i>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="flex flex-col items-center">
            <span className="text-white/60 text-sm mb-2">Descubre más</span>
            <i className="ri-arrow-down-line text-white/60 text-2xl animate-bounce"></i>
          </div>
        </motion.div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Large Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:row-span-2 bg-white border border-gray-200 rounded-3xl p-10 hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-shield-check-line text-3xl text-primary"></i>
              </div>
              <h3 className="font-display font-semibold text-2xl text-dark mb-4">
                Discreción Absoluta
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Entendemos la sensibilidad de estas situaciones. Trabajamos con la máxima <strong>confidencialidad</strong> y respeto, sin identificación externa de nuestros vehículos y con horarios adaptados a sus necesidades para evitar incomodidades con vecinos o portería.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Nuestro equipo está formado en trato humano y comprende perfectamente el momento difícil que atraviesan las familias. Nos encargamos de todo para que usted no tenga que preocuparse por nada más. <Link to="/faq" className="text-primary hover:underline cursor-pointer">Consulte nuestras preguntas frecuentes</Link>.
              </p>
            </motion.div>

            {/* Top Right Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-2 bg-primary/5 border border-primary/20 rounded-3xl p-10 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="w-16 h-16 bg-dark rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-file-shield-2-line text-3xl text-white"></i>
              </div>
              <h3 className="font-display font-semibold text-2xl text-dark mb-4">
                Protocolos Certificados
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Aplicamos <strong>protocolos profesionales de limpieza post mortem</strong> con equipos de protección individual (EPIs) certificados y productos homologados. Cumplimos toda la normativa sanitaria vigente y podemos emitir certificados de <strong>desinfección profesional</strong> cuando se requieran para seguros, administraciones o nuevos inquilinos. <Link to="/servicios" className="text-primary hover:underline cursor-pointer">Vea todos nuestros servicios especializados</Link>.
              </p>
            </motion.div>

            {/* Bottom Right Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2 bg-dark text-white rounded-3xl p-10 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="font-display font-semibold text-2xl mb-4">
                Coordinación Total
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Nos coordinamos con <strong>comunidades de vecinos, administradores de fincas e inmobiliarias</strong> para facilitar todo el proceso. Proporcionamos presupuestos por escrito, documentación completa y podemos gestionar la coordinación con otros profesionales (pintores, electricistas) si es necesario preparar el inmueble para entrega, venta o alquiler. <Link to="/contacto" className="text-white hover:text-primary transition-colors cursor-pointer">Solicite su presupuesto sin compromiso</Link>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="proceso" className="py-20 lg:py-32 bg-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-4"
            >
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span className="text-primary font-semibold text-sm">Nuestro Proceso</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
            >
              Cómo trabajamos en 4 pasos
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Un proceso claro y transparente para garantizar los mejores resultados con la máxima tranquilidad. <Link to="/empresa" className="text-primary hover:underline cursor-pointer">Descubra nuestra trayectoria profesional</Link>.
            </motion.p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gray-300 transform -translate-y-1/2">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: '0%' }}
                style={{ width: `${(activeStep / 3) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>

            {/* Steps */}
            <div className="grid lg:grid-cols-4 gap-8 lg:gap-4">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Circle */}
                  <div className="flex justify-center mb-6">
                    <div className={`w-20 h-20 rounded-full border-4 flex items-center justify-center font-display font-bold text-2xl transition-all duration-500 ${
                      activeStep >= index
                        ? 'border-primary bg-white text-primary shadow-lg shadow-primary/30'
                        : 'border-gray-300 bg-white text-gray-400'
                    }`}>
                      {step.number}
                    </div>
                  </div>

                  {/* Card */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
                      <i className={`${step.icon} text-2xl text-primary`}></i>
                    </div>
                    <h4 className="font-display font-semibold text-xl text-dark mb-3 text-center">
                      {step.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed text-center">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-4"
            >
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span className="text-primary font-semibold text-sm">Servicios Especializados</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
            >
              Nuestros servicios de <span className="text-primary">limpieza post mortem</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Soluciones completas adaptadas a cada situación específica
            </motion.p>
          </div>

          {/* Service Tabs */}
          <div className="flex overflow-x-auto pb-4 mb-12 scrollbar-hide">
            <div className="flex space-x-4 mx-auto">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`px-6 py-3 rounded-full font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                    activeService === index
                      ? 'bg-primary text-white shadow-lg shadow-primary/30'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {service.title}
                </button>
              ))}
            </div>
          </div>

          {/* Service Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, rotateY: 2 }}
                className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:border-primary/30 transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <i className={`${service.icon} text-3xl text-primary group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="font-display font-semibold text-xl text-dark mb-4 group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-primary text-lg mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/servicios"
              className="inline-flex items-center px-8 py-4 bg-dark text-white font-semibold rounded-full hover:bg-dark/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Ver Todos los Servicios
              <i className="ri-arrow-right-line text-xl ml-3"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Coverage Zones Section */}
      <section className="py-20 lg:py-32 bg-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
            >
              Cobertura en Barcelona y provincia
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Servicio de <strong>limpieza por defunción</strong> en <a href="https://www.biotrauma.es/limpieza-por-fallecimiento-en-barcelona/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline cursor-pointer">todos los distritos de Barcelona</a> y municipios del área metropolitana. <Link to="/faq" className="text-primary hover:underline cursor-pointer">¿Tiene dudas? Consulte nuestras preguntas frecuentes</Link>.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {zones.map((zone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.02 }}
                className="bg-white rounded-xl px-4 py-3 text-center hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg group"
              >
                <i className="ri-map-pin-line text-primary group-hover:text-white mr-2"></i>
                <span className="font-medium text-sm">{zone}</span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-gray-600 mb-6">
              ¿No encuentra su municipio? Consulte disponibilidad para otras zonas de la provincia
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Consultar Cobertura
              <i className="ri-arrow-right-line text-xl ml-3"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-32 bg-dark text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl mb-6"
            >
              Opiniones de familias
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-400 max-w-3xl mx-auto"
            >
              La confianza de quienes han confiado en nuestro servicio profesional. <Link to="/empresa" className="text-primary hover:underline cursor-pointer">Conozca más sobre nuestros valores y compromiso</Link>.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-dark-secondary rounded-2xl p-8 border border-gray-800 hover:border-primary/50 transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="font-display font-semibold text-primary text-lg">
                      {testimonial.initial}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-white">Familia de {testimonial.zone}</div>
                  </div>
                </div>
                <p className="text-gray-300 italic leading-relaxed">
                  "{testimonial.text}"
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20cleaning%20equipment%20organized&width=1920&height=800&seq=cta-biotrauma-v2&orientation=landscape"
            alt="Equipo profesional"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-dark/85"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display font-bold text-4xl lg:text-5xl text-white mb-6"
          >
            ¿Necesita una valoración sin compromiso?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
          >
            Contacte con nosotros y le atenderemos de inmediato. <strong className="text-white">Presupuesto gratuito</strong> y sin compromiso. Disponibles 24 horas, 7 días a la semana. <Link to="/faq" className="text-primary hover:underline cursor-pointer">Resuelva sus dudas en nuestra sección de preguntas frecuentes</Link>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="tel:+34629501941"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-primary/50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl mr-3"></i>
              +34 629 501 941
            </a>
            <a
              href="https://wa.me/34629501941?text=Hola,%20necesito%20una%20valoración%20sin%20compromiso"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-dark font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl mr-3"></i>
              WhatsApp
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Formulario de Contacto
              <i className="ri-arrow-right-line text-xl ml-3"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
